import cx from 'classnames';
import { pageTitleClass } from '@app/constants/styles';
import { clientProductReportsPath, clientsReportingPath } from '@app/constants/url';
import { useOrganizationId } from '@app/utils/hooks';
import { ProductEntity, SprintProductEntity, useClientOwnProducts } from '@data';
import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useBackNavigation } from '@app/components/core/useBackNavigation';
import ApproveAction from './ApproveAction';

export interface ProductHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  product?: ProductEntity;
  sprintProduct: SprintProductEntity;
}

// Which product and sprint the report is for, plus the one decision to make on it. Approve sits up here on
// the title line — where the PM's actions sit on their view of the same sprint — rather than beside the
// figures inside the card. What the report says (summary, hours) lives in the card below.
const ProductHeader = ({ product, sprintProduct }: ProductHeaderProps): ReactElement => {
  const { t } = useTranslation();
  const { state } = useLocation();
  const organizationId = useOrganizationId() as string;
  // A mailed link signs its recipient in without giving them the product, so back cannot assume they have it.
  // Only a client reads this header: we open a sprint on our own sprint page.
  const { data: ownProducts, isPending: isAccessPending } = useClientOwnProducts(organizationId);
  const hasProduct = Boolean(product && ownProducts?.items?.some(item => item.id === product.id));
  const backPath = hasProduct
    ? clientProductReportsPath(organizationId, product?.id)
    : clientsReportingPath(organizationId);
  const backLabel = hasProduct ? t('actions.backToProduct', { product: product?.name }) : t('actions.backToReports');
  // A walked-in visit pops history instead, so this is only where a mailed link lands.
  const { goBack } = useBackNavigation(backPath, backLabel);

  // Walks browser history, so the product page comes back with the filter and scroll it had.
  const backToProduct = (e: React.MouseEvent): void => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    goBack();
  };

  return (
    <div className="flex flex-col">
      {!isAccessPending && (
        <Link
          to={backPath}
          state={state}
          onClick={backToProduct}
          className="mb-3.5 inline-flex w-fit items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="size-4" />
          {backLabel}
        </Link>
      )}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <h1 className={cx('m-0 min-w-0', pageTitleClass)}>
          {product?.name}{' '}
          <span className="text-xl font-normal text-muted-foreground md:text-2xl">
            {t('sprintCount', { count: sprintProduct?.sprint?.number })}
          </span>
        </h1>
        <div className="flex shrink-0 items-center gap-4">
          <ApproveAction sprintProduct={sprintProduct} />
        </div>
      </div>
    </div>
  );
};

export default ProductHeader;
